import React, { useMemo } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Slide, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import StorePageHeader from 'Components/StorePageHeader';
import MerchandisingProgramPrices from './MerchandisingProgramPrices';
import { useDealer } from 'services/Data/DealerServices';
import { ApplicationIcons } from 'icons/ApplicationIcons';
import { useMerchandisingProgramCategories } from 'services/Data/MerchandisingProgramCategoryService';
import { getMerchandisingCategoryKind } from 'services/Utilities/categoryHelpers';

const useStyles = makeStyles(() => ({
  mainButton: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 1rem rgba(255, 222, 0, 0.9)',
    },
  },
  guideLink: {
    color: 'rgba(51, 51, 51, 0.8)',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(27, 27, 27, 0.8)',
      textDecoration: 'underline',
    },
  },
  customList: {
    listStyle: 'none',
    paddingLeft: '1.5rem',
    margin: 0,
  },
  listItem: {
    backgroundImage: 'url(/images/merchandising-program/check.png)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '50px 50px',
    paddingLeft: '60px',
    marginBottom: '8px',
    lineHeight: '50px',
  },
  headerWithBar: {
    position: 'relative',
    paddingLeft: '12px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      backgroundColor: '#FFD200',
    },
  },
  italicPaddedText: {
    fontWeight: 'normal',
    fontStyle: 'italic',
    marginBottom: '1rem',
  },
}));

const MerchandisingProgramQuoteLandingInfo = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  let hist = useHistory();

  const { data: dealer } = useDealer();
  const { data: merchandisingProgramCategories } = useMerchandisingProgramCategories();
  const isUsd = useMemo(() => dealer?.currencyName === 'USD', [dealer?.currencyName]);
  const showTraining = true;

  const merchandisingProgramCategoriesNoTraining = useMemo(
    () => (merchandisingProgramCategories || []).filter(category => getMerchandisingCategoryKind(category) !== 'trainStaff'),
    [merchandisingProgramCategories],
  );

  const merchandisingProgramTrainingCategories = useMemo(
    () => (merchandisingProgramCategories || []).filter(category => getMerchandisingCategoryKind(category) === 'trainStaff'),
    [merchandisingProgramCategories],
  );

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box sx={{ backgroundColor: 'background.paper', minHeight: '100vh' }}>
        <StorePageHeader
          title={t('merchandisingProgram')}
          imgSrc={'/images/headers/MerchandisingServices.jpg'}
          imgLeft
          backgroundColor="lightGray"
          borderRadius="0.5rem"
        />

        <GridContainer sx={{ mt: 1, px: 6, mb: 6 }}>
          <GridItem xs={12}>
            <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
              {t('merchandisingProgramOptions')}
            </Typography>

            <Typography variant="h1" sx={{ fontWeight: 'normal', fontSize: { sm: 18, md: 24 } }} color={'primary'}>
              {t('merchandisingProgramSeasonChange')}
            </Typography>
            <Typography variant="h4" className={classes.italicPaddedText}>
              {t('merchandisingProgramFocusOnConverting')}
            </Typography>
            <Typography variant="h1" sx={{ fontWeight: 'bold', marginBottom: '1rem', fontSize: { sm: 18, md: 24 } }}>
              {t('merchandisingProgramIncludedInBoth')}
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 'normal' }} color={'primary'}>
              <ul className={classes.customList}>
                <li className={classes.listItem}>{t('merchandisingProgramBrandRepresentation')}</li>
                <li className={classes.listItem}>{t('merchandisingProgramMerchandiseBrpDisplays')}</li>
                <li className={classes.listItem}>{t('merchandisingProgramIfBrandIsMet')}</li>
              </ul>
            </Typography>

            <Box sx={{ my: 5 }}>
              <MerchandisingProgramPrices merchandisingProgramCategoriesToUse={merchandisingProgramCategoriesNoTraining} />
              <Box>
                <GridContainer justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                  <GridItem xs={12} md={6}>
                    <Typography variant="h5" style={{ fontWeight: 'normal' }}>
                      {t('merchandisingProgramPricesWarning')}
                    </Typography>
                  </GridItem>
                </GridContainer>
              </Box>
            </Box>

            <Typography variant="h4" style={{ fontWeight: 'normal', marginTop: '3rem' }}>
              {t('merchandisingProgramAdditionalProgramRequirements')}
            </Typography>
            <Typography variant="h4" style={{ fontWeight: 'normal' }}>
              {t('merchandisingProgramAdditionalProgramRequirementsDisclaimer')}
            </Typography>

            {isUsd && showTraining && (
              <>
                <Typography
                  variant="h1"
                  sx={{ my: 5, fontWeight: 'bold', marginBottom: '1rem', fontSize: { sm: 18, md: 24 } }}>
                  {t('merchandisingProgramUsaTrainMyStaffs')}
                </Typography>
                <Typography variant="h4" style={{ fontWeight: 'normal' }} sx={{ ml: 5, mb: 3 }}>
                  <ul>
                    <li>{t('merchandisingProgramUsaTrainMyStaffsInfo1')}</li>
                  </ul>
                </Typography>

                <MerchandisingProgramPrices
                  merchandisingProgramCategoriesToUse={merchandisingProgramTrainingCategories}
                  hideDescription
                />
              </>
            )}
          </GridItem>
          {showTraining && (
            <GridItem xs={12} style={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => hist.push('/dealer/home-page/merchandising-program/quote')}
                sx={{ mt: 4, py: 3, px: 8, borderRadius: 0 }}>
                <Typography variant="h1" sx={{ color: '#000', fontSize: { xs: 16, md: 24 } }}>
                  {t('sign_up_today')}
                </Typography>
              </Button>
            </GridItem>
          )}
          <GridItem xs={12}>
            <Typography variant="h1" sx={{ my: 3, fontWeight: 'bold', marginBottom: '1rem', fontSize: { sm: 18, md: 24 } }}>
              {t('merchandisingProgramUniqueVisit')}
            </Typography>
            <Typography variant="h4" style={{ fontWeight: 'normal' }}>
              {t('merchandisingProgramUniqueVisitDetails')}
            </Typography>
            <Typography variant="h4" style={{ fontWeight: 'normal' }} sx={{ ml: 7, mt: 3 }}>
              <ul>
                <li>{t('merchandisingProgramUniqueVisitDetails1')}</li>
                <li>{t('merchandisingProgramUniqueVisitDetails2')}</li>
                <li>{t('merchandisingProgramUniqueVisitDetails3')}</li>
                <li>{t('merchandisingProgramUniqueVisitDetails4')}</li>
              </ul>
            </Typography>
            <Typography variant="h4" style={{ fontWeight: 'normal' }}>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('merchandisingProgramUniqueVisitFooterPart1'),
                }}
              />
              <ApplicationIcons.Support sx={{ paddingTop: '0.3rem' }} />
              <span
                dangerouslySetInnerHTML={{
                  __html: t('merchandisingProgramUniqueVisitFooterPart2'),
                }}
              />
            </Typography>
          </GridItem>
        </GridContainer>
      </Box>
    </Slide>
  );
};

export default MerchandisingProgramQuoteLandingInfo;
